/**
 * Utility to export HTML tables to Excel (.xls) while preserving styles, colspans, rowspans, and line breaks.
 */

export const exportTableToExcel = (tableElement, filename = 'export.xls') => {
  if (!tableElement) return;

  // Clone the table so we don't modify the visible UI
  const clone = tableElement.cloneNode(true);
  const isDailySummary = clone.querySelector('.cell-month') !== null;

  // Clean up leaf nodes in td elements to format multi-line cells properly
  clone.querySelectorAll('td').forEach(td => {
    const childBoxes = td.querySelectorAll('div, span, p');
    if (childBoxes.length > 0) {
      // Find all leaf text nodes (no further children and non-empty text)
      const leaves = Array.from(childBoxes).filter(
        el => el.children.length === 0 && el.textContent.trim() !== ''
      );
      if (leaves.length > 0) {
        const texts = leaves.map(el => el.textContent.trim());
        td.innerHTML = texts.join('<br/>');
      }
    }
  });

  // Base table styles
  const baseTableStyle = 'border-collapse: collapse; font-family: "Segoe UI", Arial, sans-serif; font-size: 11px; color: #1e293b; width: 100%;';
  clone.setAttribute('style', baseTableStyle);

  // Apply inline styles to th and td elements based on their classes
  const elements = clone.querySelectorAll('th, td');
  elements.forEach(el => {
    let css = 'border: 1px solid #cbd5e1; padding: 8px; vertical-align: middle; ';

    // Default alignments & font weights
    if (el.tagName === 'TH') {
      css += 'text-align: center; font-weight: bold; background-color: #f8fafc; color: #475569; ';
    } else {
      css += 'text-align: left; background-color: #ffffff; ';
    }

    // Corner cell
    if (el.classList.contains('corner-cell')) {
      css += 'background-color: #ffffff !important; color: #1e293b !important; font-weight: bold !important; border-right: 2px solid #94a3b8 !important; border-bottom: 2px solid #94a3b8 !important; ';
    }

    // Section title
    if (el.classList.contains('section-title')) {
      css += 'font-size: 11px !important; font-weight: bold !important; text-transform: uppercase !important; ';
      if (el.classList.contains('section-title--business')) {
        css += 'background-color: #e0ecff !important; color: #1e3a8a !important; ';
      } else if (el.classList.contains('section-title--month')) {
        css += 'background-color: #d1fae5 !important; color: #065f46 !important; ';
      }
    }

    // Column headers
    if (el.classList.contains('col-head')) {
      css += 'font-size: 10px !important; font-weight: bold !important; ';
      if (el.classList.contains('col-head--business')) {
        css += 'background-color: #f1f5f9 !important; border-top: 3px solid #3b82f6 !important; ';
      } else if (el.classList.contains('col-head--month')) {
        css += 'background-color: #edfcf6 !important; border-top: 3px solid #10b981 !important; ';
      }
    }

    // Row headers
    if (el.classList.contains('row-header')) {
      css += 'background-color: #fafbfc !important; font-weight: bold !important; border-right: 2px solid #94a3b8 !important; text-align: left !important; ';
      const parentRow = el.closest('tr');
      if (parentRow && parentRow.classList.contains('data-row--total')) {
        css += 'background-color: #eff6ff !important; color: #1e3a8a !important; ';
      }
    }

    // Cell data types & zebra striping
    const parentRow = el.closest('tr');
    if (parentRow) {
      const isTotalRow = parentRow.classList.contains('data-row--total');
      const isEvenRow = Array.from(parentRow.parentNode.children).indexOf(parentRow) % 2 === 1;

      // Business day cells
      if (el.classList.contains('cell-business-day')) {
        if (isTotalRow) {
          css += 'background-color: #bfdbfe !important; color: #0f172a !important; font-weight: bold !important; ';
        } else {
          css += isEvenRow 
            ? 'background-color: #e6efff !important; ' 
            : 'background-color: #f5f9ff !important; ';
        }
      }

      // Month cells (Daily Summary only)
      if (el.classList.contains('cell-month')) {
        if (isTotalRow) {
          css += 'background-color: #a7f3d0 !important; color: #0f172a !important; font-weight: bold !important; ';
        } else {
          css += isEvenRow 
            ? 'background-color: #dbfaeb !important; ' 
            : 'background-color: #f4fdf8 !important; ';
        }
      }
    }

    // Numeric cell styling
    if (el.classList.contains('numeric-cell')) {
      if (isDailySummary) {
        css += 'text-align: right !important; font-variant-numeric: tabular-nums; ';
      } else {
        // Machine parts report
        css += 'text-align: center !important; font-weight: bold !important; font-size: 11px !important; ';
      }
    }

    // Border section divider
    if (el.classList.contains('border-right-section')) {
      css += 'border-right: 2px solid #94a3b8 !important; ';
    }

    // Empty cell
    if (el.classList.contains('cell-empty')) {
      css += 'color: #94a3b8 !important; ';
    }

    // Apply inline style
    const existing = el.getAttribute('style') || '';
    el.setAttribute('style', existing + '; ' + css);
  });

  // Excel template with gridlines option and carriage return handling
  const template = `
    <html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40">
    <head>
      <meta charset="UTF-8">
      <!--[if gte mso 9]>
      <xml>
        <x:ExcelWorkbook>
          <x:ExcelWorksheets>
            <x:ExcelWorksheet>
              <x:Name>Sheet1</x:Name>
              <x:WorksheetOptions>
                <x:DisplayGridlines/>
              </x:WorksheetOptions>
            </x:ExcelWorksheet>
          </x:ExcelWorksheets>
        </x:ExcelWorkbook>
      </xml>
      <![endif]-->
      <style>
        br { mso-data-placement: same-cell; }
      </style>
    </head>
    <body>
      ${clone.outerHTML}
    </body>
    </html>
  `;

  // Create file download link
  const blob = new Blob([template], { type: 'application/vnd.ms-excel;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
